import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Game, Listing, CategorySlug } from '../types';
import { ListingCard } from '../components/listing/ListingCard';
import { BoostingCalculator } from '../components/boosting/BoostingCalculator';
import { ShieldCheck, Zap, Layers, Sparkles, Filter } from 'lucide-react';

export const GameDetail: React.FC = () => {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        const [gRes, lRes] = await Promise.all([
          fetch(`/api/games/${gameSlug}`),
          fetch(`/api/listings?game=${gameSlug}${activeCategory !== 'all' ? `&category=${activeCategory}` : ''}`)
        ]);

        if (gRes.ok) {
          const gData = await gRes.json();
          if (gData.success) setGame(gData.game);
        }
        if (lRes.ok) {
          const lData = await lRes.json();
          if (lData.success) setListings(lData.listings || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, [gameSlug, activeCategory]);

  if (!game && !loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-black">Game not found</h2>
        <Link to="/games" className="mt-4 inline-block text-brand-blue font-bold">Browse all games</Link>
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-16">
      
      {/* Game Banner Header */}
      <div className="relative h-64 sm:h-80 w-full overflow-hidden bg-slate-950">
        <img
          src={game?.banner_url || 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1400'}
          alt={game?.name}
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-lightBg dark:from-brand-darkBg via-transparent to-transparent" />

        <div className="absolute bottom-6 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end gap-6">
          <img
            src={game?.icon_url}
            alt=""
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-white dark:border-brand-darkCard shadow-2xl flex-shrink-0"
          />
          <div className="space-y-1">
            <span className="text-xs uppercase font-extrabold tracking-widest text-brand-blue dark:text-brand-accent">
              {game?.publisher}
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-brand-lightText dark:text-brand-darkText drop-shadow">
              {game?.name}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Category Filter Pills */}
        <div className="flex flex-wrap items-center gap-2 border-b border-brand-lightBorder dark:border-brand-darkBorder pb-4">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              activeCategory === 'all'
                ? 'bg-brand-blue text-white shadow-glow-blue'
                : 'bg-brand-lightSec dark:bg-brand-darkCard text-brand-lightSecText dark:text-brand-darkSecText hover:text-brand-lightText dark:hover:text-white'
            }`}
          >
            All Offers
          </button>

          {game?.categories?.map((cat) => (
            <button
              key={cat.category_slug}
              onClick={() => setActiveCategory(cat.category_slug)}
              className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all ${
                activeCategory === cat.category_slug
                  ? 'bg-brand-blue text-white shadow-glow-blue'
                  : 'bg-brand-lightSec dark:bg-brand-darkCard text-brand-lightSecText dark:text-brand-darkSecText hover:text-brand-lightText dark:hover:text-white'
              }`}
            >
              {cat.category_slug}
            </button>
          ))}
        </div>

        {/* If Boosting Category is selected, show interactive boosting calculator */}
        {activeCategory === 'boosting' && (
          <div className="space-y-4">
            <h3 className="font-extrabold text-xl text-brand-lightText dark:text-brand-darkText">
              Custom Rank & Win Calculator
            </h3>
            <BoostingCalculator initialGame={gameSlug} />
          </div>
        )}

        {/* Listings Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-lg text-brand-lightText dark:text-brand-darkText">
              Available Listings ({listings.length})
            </h2>
            <Link
              to={`/browse?game=${gameSlug}`}
              className="text-xs font-semibold text-brand-blue dark:text-brand-accent hover:underline flex items-center gap-1"
            >
              <Filter className="w-3.5 h-3.5" />
              Advanced Filters
            </Link>
          </div>

          {listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {listings.map((l) => (
                <ListingCard key={l.id} listing={l} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 rounded-3xl bg-brand-lightSec dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder">
              <Layers className="w-12 h-12 text-brand-lightSecText mx-auto mb-3" />
              <h3 className="font-bold text-base text-brand-lightText dark:text-brand-darkText">No listings found in this category</h3>
              <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">Check back soon or explore other categories.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
